/**
 * Population scope — which claims a set of trades is allowed to support.
 *
 * ## Why this exists
 *
 * The column projection was safe because it narrowed COLUMNS while keeping every
 * ROW, so every aggregate kept its full population and a gate could prove the
 * numbers byte-identical. A bounded read narrows rows, and that changes every
 * number by construction. There is no "identical numbers" gate available.
 *
 * There are exactly two classes of claim in this product:
 *
 *   CLASS W — the claim is a function of the WINDOW. Dashboard KPIs, win rate,
 *             expectancy over the active range. If the loaded set is a superset
 *             of the active range, filtering it gives the same answer as
 *             filtering the whole book: the same predicate applied twice.
 *
 *   CLASS L — the claim is a function of the WHOLE POPULATION. Account balance,
 *             peak, drawdown floor and buffer, profit-target progress,
 *             consistency, min-trading-days, position-size `everOver`, lifetime
 *             goals. Under a bound these are not "less precise". They are
 *             PLAUSIBLY WRONG IN THE OPTIMISTIC DIRECTION and self-consistent
 *             while wrong — a drawdown buffer that reads larger than the room the
 *             trader actually has, a consistency rule that reports a false pass
 *             on a payout gate, an `everOver` breach flag that flips true → false.
 *
 * There is no third option for Class L: either you have the population, or a
 * server that had it computed the number, or you do not render it. This module
 * is the third option, made unavoidable.
 *
 * ## Two halves, and BOTH are real
 *
 * The first draft of this used `declare const scope: unique symbol` as the
 * discriminant. An adversarial pass killed it: that is TYPE-ONLY, it emits
 * nothing, so both constructors would return an indistinguishable `{ rows }` and
 * `isWholeBook` could not be implemented at all. A "runtime half" with no runtime
 * existence is not a guard — it is the phantom-control mistake one layer up.
 *
 * So, mirroring `PROJECTION_TAINT` (which is a plain string constant precisely so
 * it EXISTS and TRAVELS):
 *
 *   compile time — a discriminated union. A Class-L selector declares
 *                  `WholeBook<T>`, so handing it a `Population<T>` is a compile
 *                  error until the caller narrows. The consumer has to write the
 *                  else-branch; the block is a type error, not a review comment.
 *   run time     — `SCOPE` is a real enumerable property, and `asWholeBook`
 *                  REFUSES to mint one unless the receipt proves the read
 *                  actually covered the whole book. That is what survives the
 *                  `as WholeBook<T>` cast, which is how the compile-time half
 *                  will actually be defeated in practice.
 *
 * `domain/` learns none of this. It keeps taking `readonly T[]` and stays correct
 * for whatever population it is handed — which is what keeps it importable into a
 * Deno Edge Function (D11/D43). The scope discipline belongs in the layer that
 * knows about loading.
 */
import type { ReadReceipt } from '@io/sync/receipt';

/** The runtime discriminant. A real own property, not a phantom symbol. */
export const SCOPE = '__tcScope' as const;

export type ScopeTag = 'whole-book' | 'bounded';

export interface WholeBook<T> {
  readonly rows: readonly T[];
  readonly [SCOPE]: 'whole-book';
}

export interface BoundedSince<T> {
  readonly rows: readonly T[];
  /** The `date >=` bound that was applied. */
  readonly since: string;
  readonly [SCOPE]: 'bounded';
}

export type Population<T> = WholeBook<T> | BoundedSince<T>;

/** Narrowing predicate. Reads the REAL property, so it can actually be written. */
export function isWholeBook<T>(p: Population<T>): p is WholeBook<T> {
  return p[SCOPE] === 'whole-book';
}

export function isBounded<T>(p: Population<T>): p is BoundedSince<T> {
  return p[SCOPE] === 'bounded';
}

/**
 * Mint a whole-book population — or refuse.
 *
 * ⚠ The refusal is the point. A caller who has a bounded read and wants a Class-L
 * number will reach for this, and it must say no rather than let a plausible
 * wrong number through.
 */
export function asWholeBook<T>(rows: readonly T[], receipt: ReadReceipt): WholeBook<T> {
  if (!receipt.terminated) {
    throw new Error(
      `asWholeBook: the read did not terminate cleanly (${receipt.pages} page(s)), so rows ` +
        `may be missing. A population-scoped number computed from a truncated read is wrong ` +
        `in the OPTIMISTIC direction — a drawdown buffer larger than the room that exists.`,
    );
  }
  if (!receipt.coversWholeBook) {
    throw new Error(
      `asWholeBook: this read was bounded (since ${String(receipt.since)}) and no backfill ` +
        `has completed it. Balance, drawdown, profit target, consistency, min-trading-days ` +
        `and position-size breach flags all require the whole population. Either await the ` +
        `backfill, or render the loading state — do not compute from what has arrived.`,
    );
  }
  return { rows, [SCOPE]: 'whole-book' };
}

export function asBounded<T>(rows: readonly T[], receipt: ReadReceipt): BoundedSince<T> {
  if (receipt.since == null) {
    throw new Error('asBounded: receipt has no `since` — this was an unbounded read.');
  }
  return { rows, since: receipt.since, [SCOPE]: 'bounded' };
}

/**
 * Build the right variant from a receipt, without the caller choosing.
 *
 * Preferred over calling the two constructors directly: it removes the decision
 * from the call site, so a caller cannot pick `asWholeBook` optimistically.
 */
export function populationFrom<T>(rows: readonly T[], receipt: ReadReceipt): Population<T> {
  if (receipt.coversWholeBook && receipt.terminated) return asWholeBook(rows, receipt);
  if (receipt.since != null) return asBounded(rows, receipt);
  // ⚠ THE THIRD STATE, and it is not a population at all: an UNBOUNDED read that
  // did not terminate. There is no `since` to describe what arrived and no
  // coverage to justify a whole book — the rows have unknown extent, so neither
  // a Class-W nor a Class-L claim can be made about them.
  //
  // Refused rather than modelled as a third variant. A variant would have to be
  // handled at every call site to say "we cannot answer", which is what the
  // absence of a population already says.
  throw new Error(
    `populationFrom: an unbounded read that did not terminate (${receipt.pages} page(s)) has ` +
      `unknown extent — there is no window to scope a claim to and no coverage to justify a ` +
      `whole book. Retry the read; do not compute from what arrived.`,
  );
}

/**
 * The Class-W escape hatch, named so it cannot be used by accident.
 *
 * A window-scoped claim is valid over EITHER variant, but only when the loaded
 * set is a superset of the window being claimed. That containment is the
 * caller's obligation and cannot be checked here — hence the explicit
 * `windowStart`, and the throw when a bounded read does not reach back far
 * enough. A width test is not sufficient: a 30-day custom range set to January
 * is 30 days wide and entirely outside a 45-day bound.
 */
/**
 * Can a Class-W claim be made over this window WITHOUT throwing?
 *
 * ⚠ The question a UI has to ask before it renders a heading. `rowsForWindow`
 * throws on an uncovered window, which is right for a computation — the whole
 * point is that it cannot silently answer for a narrower window than the label
 * claims — but a throw inside render is a blank screen, and a blank screen is
 * indistinguishable from a bug.
 *
 * So a surface asks first and renders "not loaded" for itself. Without this
 * every caller writes a try/catch around a render, which is exactly the
 * every-call-site handling the throwing design exists to avoid.
 */
export function coversWindow<T>(p: Population<T> | null, windowStart: string): boolean {
  if (!p) return false;
  return !isBounded(p) || windowStart >= p.since;
}
export function rowsForWindow<T>(p: Population<T>, windowStart: string): readonly T[] {
  if (isBounded(p) && windowStart < p.since) {
    throw new Error(
      `rowsForWindow: the requested window starts ${windowStart}, but the loaded set only ` +
        `reaches back to ${p.since}. Filtering it would silently answer for a narrower ` +
        `window than the label claims. Widen the read or block the surface.`,
    );
  }
  return p.rows;
}

// ── Row types, re-exported for ui/ ───────────────────────────────────────────
//
// ⚠ NOT a convenience. `ui/` may not import `domain/` at all — the layering
// check bans it, and it caught the first Phase 4 file for naming `MetricsRow`
// in a type position.
//
// The ban is mechanical on purpose: a rule that allowed `import type` would need
// every reviewer to keep distinguishing a type import from a value import in a
// codebase where the whole hazard is a component calling a domain function with
// a partial array. "ui/ imports nothing from domain/" is checkable; "ui/ imports
// only types from domain/" is an argument every time.
//
// So the row type travels with the wrapper that scopes it. A component naming
// `Population<MetricsRow>` gets both from here, which is also the honest
// dependency: what ui/ needs is "the thing selectors hands me", not the
// projection's internals.
export type { MetricsRow, MetricsKey } from '../domain/projection';

// ── The day-boundary rule, re-exported for ui/ ───────────────────────────────
//
// ⚠ NOT convenience. `effectiveTradeDate`'s own docstring says "every
// aggregation and filter must call this rather than reading `date` directly —
// a surface that reads the raw field silently opts out of it", and two ported
// tabs did exactly that: Trades displayed and sorted on `t.date`, Calendar
// bucketed on it. In session mode both put evening trades on the wrong day,
// which Josh caught by putting the two clients side by side.
//
// ui/ may not import domain/, so without this re-export the path of least
// resistance IS the raw field — the rule is unreachable and the mistake is the
// default. Exposing it here makes calling the rule the easy option.
export { effectiveTradeDate, type DatedTrade } from '../domain/session';
export type { DayBoundaryMode } from '../domain/range';

// ⚠ `DEFAULT_TZ` is re-exported for the SAME reason, and only for callers that
// pair it with a `mode` default (TCR-96). `tradeDayFor` makes its timezone
// REQUIRED so nobody silently computes a session day in a zone the user does not
// use — but a ui/ caller holding `prefs` that has not loaded yet already falls
// back to `'calendar'` for the mode, and in calendar mode the timezone is never
// consulted. Pairing the two defaults is what makes that safe; reaching for this
// constant anywhere else is the thing the required parameter exists to stop.
export { DEFAULT_TZ } from '../domain/session';
